import {
  Entity,
  House,
  Playground,
  WatchTower,
} from "@eai-toolkit-material-ui/icons";
import AutoAwesome from "@mui/icons-material/AutoAwesome";
import ExpandMore from "@mui/icons-material/ExpandMore";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import MuiOpenInNewIcon from "@mui/icons-material/OpenInNew";
import VpnKeyOutlined from "@mui/icons-material/VpnKeyOutlined";
import { Grid, styled } from "@mui/material";
import { tracker } from "@openreplay/tracker";
import React, { useContext, useState } from "react";
import type { NavLinkProps } from "react-router-dom";
import { NavLink, useLocation } from "react-router-dom";

import {
  ACCESS_REQUESTS_PAGE_TITLE,
  ACCESS_REQUESTS_ROUTE,
  ASSISTANT_PAGE_TITLE,
  ASSISTANT_ROUTE,
  DATA_EXPLORER_PAGE_TITLE,
  DATA_EXPLORER_ROUTE,
  EAI_CONSOLE_HOST,
  EAI_CONSOLE_PROTOCOL,
  JOBS_OVERVIEW_ROUTE,
  JOBS_ROUTE,
  NOTEBOOKS_ROUTE,
  PERMISSIONS_ROUTE,
} from "@helpers/constants";
import { NAVIGATION_EVENTS } from "@helpers/events";

import { MenuToggle } from "../../../../icons";
import { GetStartedContext } from "../GetStarted/GetStartedContext";
import styles from "./SideNavigation.module.scss";

const MenuItemBase = styled(NavLink)(() => ({
  display: "block",
  color: "white",
  width: "100%",
  "& svg": { fill: "white" },
}));

const MenuItemUpper = styled("div")(() => ({
  display: "grid",
  gridTemplate: "100% / auto 1fr auto",
  gap: "1rem",
  alignItems: "center",
  opacity: 0.6,
  width: "100%",
  padding: "0 2rem",
  "&:hover, .active &": { opacity: 1 },
  "& .icon-group": {
    display: "flex",
    gap: "0.5rem",
    alignItems: "center",
  },
}));

const MenuItemText = styled("span")(() => ({
  color: "white",
  padding: "12px 0",
  fontSize: "16px",
  lineHeight: "24px",
  justifySelf: "start",
}));

const MenuToggleButton = styled("button")<{ isOpen: boolean }>(
  ({ isOpen }) => ({
    height: "var(--closed-sidebar-size)",
    width: "var(--open-sidebar-size)",
    padding: 0,
    textAlign: "right",
    background: "none",
    border: "none",
    cursor: "pointer",
    position: "absolute",
    bottom: 0,
    right: 0,
    zIndex: 101,
    outline: "none",
    opacity: 0.6,
    "&:hover, &:focus": { opacity: 1 },
    "& svg": {
      transform: isOpen ? "rotate(0deg)" : "rotate(180deg) translate(-50%, 0)",
      marginRight: "calc(var(--closed-sidebar-size) / 2)",
      transition: "transform 0.3s ease",
    },
  }),
);

const OpenInNewIcon = styled(MuiOpenInNewIcon)(({ theme }) => ({
  fontSize: theme.typography.body1.fontSize,
  marginLeft: theme.spacing(1),
}));

const SmallMenuLink = styled(NavLink)(() => ({
  opacity: 0.6,
  "&:hover, &.active": { opacity: 1 },
}));

function MenuItem({
  icon,
  label,
  children,
  defaultRoute,
  isActive = false,
  // apps = [],
  eventName = "",
}: {
  icon?: React.ReactNode;
  label: string;
  children: React.ReactNode;
  defaultRoute: string;
  isActive?: boolean;
  apps?: Array<{ name: string; id: string; displayName: string }>;
  eventName?: string;
}) {
  const [showChildren, setShowChildren] = useState(isActive);

  const openSubmenu = () => {
    // Open submenu when navigating to the route
    setShowChildren(true);
    tracker.event(eventName);
  };

  const toggleSubmenu = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setShowChildren(!showChildren);
  };

  const ExpandCollapseButton = () => (
    <div
      onMouseDown={toggleSubmenu}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          toggleSubmenu(e);
        }
      }}
      tabIndex={0}
      role="button"
      aria-label="Toggle submenu"
      aria-expanded={showChildren}
    >
      <ExpandMore />
    </div>
  );

  return (
    <>
      <MenuItemBase
        to={defaultRoute}
        className={isActive ? "active" : ""}
        onClick={openSubmenu}
        aria-label={label}
      >
        <MenuItemUpper>
          {icon && icon}
          <MenuItemText className="hiddenMenuElement">{label}</MenuItemText>
          <ExpandCollapseButton />
        </MenuItemUpper>
      </MenuItemBase>
      {showChildren ? <div>{children}</div> : null}
    </>
  );
}

function MenuLink({
  icon,
  label,
  ...props
}: NavLinkProps & { label: React.ReactNode; icon?: React.ReactNode }) {
  return (
    <MenuItemBase {...props}>
      <MenuItemUpper>
        <div className="icon-group">{icon}</div>
        <MenuItemText className="hiddenMenuElement">
          <Grid container alignItems="center">
            {label} {props.target === "_blank" && <OpenInNewIcon />}
          </Grid>
        </MenuItemText>
      </MenuItemUpper>
    </MenuItemBase>
  );
}

function SideNavigation(
  props: { handleToggleMenu: () => void; isMenuFixed: boolean },
  ref?: React.Ref<HTMLElement>,
) {
  const { pathname: currentLocation } = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(props.isMenuFixed);
  const toggleMenu = () => {
    setIsMenuOpen((prev) => !prev);
    props.handleToggleMenu();
    tracker.event(NAVIGATION_EVENTS.SIDE_NAVIGATION_TOGGLE_CLICKED);
  };
  const { toggleDrawer } = useContext(GetStartedContext);

  return (
    <aside>
      <nav className={styles.nav} ref={ref}>
        <div>
          <MenuLink
            to="/"
            icon={<House />}
            label={"Dashboard"}
            onClick={() =>
              tracker.event(NAVIGATION_EVENTS.SIDE_NAVIGATION_DASHBOARD_CLICKED)
            }
          />
          <MenuLink
            to={DATA_EXPLORER_ROUTE}
            icon={<Entity />}
            label={DATA_EXPLORER_PAGE_TITLE}
            onClick={() =>
              tracker.event(
                NAVIGATION_EVENTS.SIDE_NAVIGATION_DATA_EXPLORER_CLICKED,
              )
            }
          />
          <MenuLink
            to={NOTEBOOKS_ROUTE}
            icon={<Playground />}
            label={"Notebooks"}
            onClick={() =>
              tracker.event(NAVIGATION_EVENTS.SIDE_NAVIGATION_NOTEBOOKS_CLICKED)
            }
          />
          <MenuItem
            icon={<WatchTower />}
            label="Jobs"
            defaultRoute={JOBS_ROUTE}
            isActive={
              currentLocation.includes(JOBS_ROUTE) ||
              currentLocation.includes(JOBS_OVERVIEW_ROUTE)
            }
            eventName={NAVIGATION_EVENTS.SIDE_NAVIGATION_JOBS_CLICKED}
          >
            <ul className={styles.appList}>
              <li>
                <SmallMenuLink
                  onClick={() =>
                    tracker.event(
                      NAVIGATION_EVENTS.SIDE_NAVIGATION_JOBS_JOBS_CLICKED,
                    )
                  }
                  to={JOBS_ROUTE}
                >
                  Jobs
                </SmallMenuLink>
              </li>
              <li>
                <SmallMenuLink
                  onClick={() =>
                    tracker.event(
                      NAVIGATION_EVENTS.SIDE_NAVIGATION_JOBS_OVERVIEW_CLICKED,
                    )
                  }
                  to={JOBS_OVERVIEW_ROUTE}
                >
                  Overview
                </SmallMenuLink>
              </li>
            </ul>
          </MenuItem>
          <MenuItem
            icon={<VpnKeyOutlined />}
            label="Permissions"
            defaultRoute={PERMISSIONS_ROUTE}
            isActive={
              currentLocation.includes(PERMISSIONS_ROUTE) ||
              currentLocation.includes(ACCESS_REQUESTS_ROUTE)
            }
            eventName={NAVIGATION_EVENTS.SIDE_NAVIGATION_PERMISSIONS_CLICKED}
          >
            <ul className={styles.appList}>
              <li>
                <SmallMenuLink
                  onClick={() =>
                    tracker.event(
                      NAVIGATION_EVENTS.SIDE_NAVIGATION_PERMISSIONS_PERMISSIONS_CLICKED,
                    )
                  }
                  to={PERMISSIONS_ROUTE}
                >
                  Permissions
                </SmallMenuLink>
              </li>
              <li>
                <SmallMenuLink
                  onClick={() =>
                    tracker.event(
                      NAVIGATION_EVENTS.SIDE_NAVIGATION_PERMISSIONS_ACCESS_REQUESTS_CLICKED,
                    )
                  }
                  to={ACCESS_REQUESTS_ROUTE}
                >
                  {ACCESS_REQUESTS_PAGE_TITLE}
                </SmallMenuLink>
              </li>
            </ul>
          </MenuItem>
          <MenuLink
            to={ASSISTANT_ROUTE}
            icon={<AutoAwesome />}
            label={ASSISTANT_PAGE_TITLE}
            onClick={() =>
              tracker.event(NAVIGATION_EVENTS.SIDE_NAVIGATION_ASSISTANT_CLICKED)
            }
          />
          <MenuLink
            to={`${EAI_CONSOLE_PROTOCOL}//docs.${EAI_CONSOLE_HOST}/index.html`}
            icon={<MenuBookOutlinedIcon />}
            label="Docs"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() =>
              tracker.event(NAVIGATION_EVENTS.SIDE_NAVIGATION_DOCS_CLICKED)
            }
          />
        </div>
        <MenuItemUpper
          style={{ cursor: "pointer" }}
          onClick={() => toggleDrawer()}
        >
          <div className="icon-group" style={{ color: "white" }}>
            <HelpOutlineIcon />
          </div>
          <MenuItemText className="hiddenMenuElement">
            <Grid container alignItems="center">
              Get started
            </Grid>
          </MenuItemText>
        </MenuItemUpper>
      </nav>

      <MenuToggleButton isOpen={isMenuOpen} onClick={toggleMenu}>
        <MenuToggle />
      </MenuToggleButton>
    </aside>
  );
}

export default React.forwardRef(SideNavigation);
